import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useAuth0User } from '@hooks/useAuth0User';
import { isDemoUser } from '@lib/auth0/utils';
import { readMockFlagOverride } from '@lib/analytics/posthog-mock-flags';
import { readLastLoadedFlag } from '@lib/posthog';
import { DEMO_SESSION_FLAG_OVERRIDES } from '@lib/posthog-feature-flags';
import type { PostHogFeatureFlags } from '@lib/posthog-feature-flags';

/**
 * Read a PostHog boolean feature flag.
 *
 * New feature flags should be added to `PostHogFeatureFlags` in
 * `@lib/posthog-feature-flags` and consumed via this hook. Flags that carry
 * a JSON payload belong with `usePostHogFeatureFlagPayload`.
 *
 * Returns the supplied default while PostHog is still loading flags on the
 * client, and when PostHog is not configured (e.g. local development or
 * e2e runs without the env vars set), so consumers never observe an
 * `undefined` state. Match the kill-switch convention in this codebase
 * (`pointsPageIsClosed`, `fplSeasonIsClosed`...) where the default value
 * renders the working page; tests then exercise the default path without
 * needing to mock the flag service.
 *
 * Once PostHog has loaded flags, a later spell with none (a visitor without
 * analytics consent has PostHog reset after load) reads the flags it last
 * loaded rather than the default; see `readLastLoadedFlag`.
 *
 * App-review demo sessions bypass PostHog for the season-dependent flags in
 * `DEMO_SESSION_FLAG_OVERRIDES`, so the demo user sees the mid-season
 * experience their demo backend serves while production traffic keeps the
 * real flag values.
 *
 * Mocked-backend runs (e2e, `npm run dev:mock:*`) can set a flag's value
 * instead; see `readMockFlagOverride`.
 */
export const usePostHogFeatureFlag = (
  flag: PostHogFeatureFlags,
  defaultValue: boolean
): boolean => {
  const { user } = useAuth0User();
  const value = useFeatureFlagEnabled(flag);

  const mockOverride = readMockFlagOverride(flag);
  if (mockOverride !== undefined) return mockOverride;

  const demoOverride = DEMO_SESSION_FLAG_OVERRIDES[flag];
  if (demoOverride !== undefined && isDemoUser(user)) {
    return demoOverride;
  }

  return value ?? readLastLoadedFlag(flag) ?? defaultValue;
};
